import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Star } from "lucide-react";

const empty = { event_type: "edicao", edition_number: 1, name: "", start_date: "", end_date: "", time: "19:30", location: "", description: "", cover_image: "", max_participants: 100, registrations_open: true, is_next: false, status: "upcoming" };

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const nextNum = events.length ? Math.max(...events.map((e) => e.edition_number)) + 1 : 1;
    setForm({ ...empty, edition_number: nextNum });
    setEditing("new");
  };
  const openEdit = (e) => {
    setForm({ ...empty, ...e });
    setEditing(e.id);
  };
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, edition_number: parseInt(form.edition_number), max_participants: parseInt(form.max_participants || 0) || null };
    try {
      if (editing === "new") await api.post("/events", payload);
      else await api.put(`/events/${editing}`, payload);
      toast.success("Salvo!");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const del = async (id) => {
    if (!confirm("Excluir este evento?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Excluído");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label">Eventos</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">Edições</h1>
        </div>
        <button data-testid="admin-new-event" onClick={openNew} className="btn-primary"><Plus size={16}/> Novo evento</button>
      </div>

      {editing && (
        <form onSubmit={save} className="ci-card p-6 space-y-4" data-testid="admin-event-form">
          <div>
            <label className="label block mb-2">Tipo de evento *</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: "edicao", t: "Edição", d: "Edição oficial do retiro (com inscrições)" },
                { v: "encontro", t: "Encontro", d: "Encontro simples (sem inscrições)" },
              ].map((o) => (
                <button type="button" key={o.v} data-testid={`admin-event-type-${o.v}`}
                        onClick={() => setForm({ ...form, event_type: o.v })}
                        className={`flex-1 min-w-[200px] text-left p-4 rounded-xl border transition-all ${form.event_type === o.v ? "border-[#1E90FF] bg-[#1E90FF]/10" : "border-white/10 bg-black/40 hover:border-white/30"}`}>
                  <div className="font-display font-bold text-base">{o.t}</div>
                  <div className="text-xs text-slate-400 mt-1">{o.d}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="label">{form.event_type === "encontro" ? "Nº (interno)" : "Nº Edição"}</label>
              <input type="number" required value={form.edition_number} onChange={(e)=>setForm({...form, edition_number:e.target.value})} className="ci-input mt-1"/>
            </div>
            <div className="sm:col-span-2"><label className="label">{form.event_type === "encontro" ? "Título do encontro" : "Nome"}</label><input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="ci-input mt-1"/></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="label">{form.event_type === "encontro" ? "Data do encontro" : "Data inicial"}</label><input type="date" required value={form.start_date?.slice(0,10)} onChange={(e)=>setForm({...form, start_date:e.target.value})} className="ci-input mt-1"/></div>
            {form.event_type !== "encontro" && (
              <div><label className="label">Data final</label><input type="date" value={form.end_date?.slice(0,10) || ""} onChange={(e)=>setForm({...form, end_date:e.target.value})} className="ci-input mt-1"/></div>
            )}
            <div><label className="label">Horário</label><input value={form.time || ""} onChange={(e)=>setForm({...form, time:e.target.value})} className="ci-input mt-1"/></div>
          </div>
          <div><label className="label">Local</label><input value={form.location || ""} onChange={(e)=>setForm({...form, location:e.target.value})} className="ci-input mt-1"/></div>
          <div><label className="label">Descrição</label><textarea rows={3} value={form.description || ""} onChange={(e)=>setForm({...form, description:e.target.value})} className="ci-input mt-1 resize-none"/></div>
          <div className="grid sm:grid-cols-3 gap-4">
            {form.event_type !== "encontro" && (
              <div><label className="label">Limite</label><input type="number" value={form.max_participants || ""} onChange={(e)=>setForm({...form, max_participants:e.target.value})} className="ci-input mt-1"/></div>
            )}
            <div><label className="label">Status</label>
              <select value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})} className="ci-input mt-1">
                <option value="upcoming">Próximo</option><option value="ongoing">Em andamento</option><option value="past">Passado</option>
              </select>
            </div>
            <div className="flex gap-4 items-end flex-wrap">
              {form.event_type !== "encontro" && (
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.registrations_open} onChange={(e)=>setForm({...form, registrations_open:e.target.checked})} className="accent-blue-400 w-4 h-4"/> Inscrições abertas</label>
              )}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_next} onChange={(e)=>setForm({...form, is_next:e.target.checked})} className="accent-blue-400 w-4 h-4"/> É o próximo</label>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Salvar</button>
            <button type="button" onClick={()=>setEditing(null)} className="btn-ghost">Cancelar</button>
          </div>
        </form>
      )}

      <div className="grid gap-3">
        {events.map((e) => (
          <div key={e.id} className="ci-card p-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              {e.is_next && <Star size={16} className="text-blue-400 fill-blue-400" />}
              <div>
                <div className="label">{e.edition_number}ª Edição • {e.status}</div>
                <h3 className="font-display text-lg font-bold mt-1">{e.name}</h3>
                <div className="text-xs text-slate-400 mt-1">{new Date(e.start_date).toLocaleDateString("pt-BR")} • {e.location}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`admin-event-edit-${e.id}`} onClick={() => openEdit(e)} className="btn-ghost text-xs py-2 px-3"><Edit2 size={14}/></button>
              <button data-testid={`admin-event-delete-${e.id}`} onClick={() => del(e.id)} className="btn-ghost text-xs py-2 px-3 border-red-500/30 hover:border-red-400 text-red-300"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
